import { API_BASE_URL as BASE_URL } from '../config';

// Google Earth Engine Service for frontend
const API_BASE_URL = `${BASE_URL}/api/gee`;

export interface GEECollection {
  id: string;
  name: string;
  description: string;
  collection: string;
  bands: string[];
  startDate: string;
  cloudFilter: number;
}

export interface GEEMetadata {
  id: string;
  name: string;
  description: string;
  collection: string;
  bands: string[];
  startDate: string;
  cloudFilter: number;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface Bounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface TimeSeriesData {
  id: string;
  date: string;
  cloudCover: number;
}

export interface AnimationFrame {
  id: string;
  index: number;
  date: string;
  thumbnailUrl?: string | null;
}

// COMMENTED OUT: Authentication is disabled
// Get authentication token from localStorage
// const getAuthToken = (): string | null => {
//   return localStorage.getItem('authToken');
// };

// Make authenticated API request
const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit = {}
) => {
  // COMMENTED OUT: Authentication is disabled
  // const token = getAuthToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  // COMMENTED OUT: No authentication needed
  // Add authorization header if token exists
  // if (token) {
  //   headers['Authorization'] = `Bearer ${token}`;
  // }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    );
  }

  return response.json();
};

// Get available satellite collections
export const getAvailableCollections = async (): Promise<GEECollection[]> => {
  try {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/collections`
    );
    return response.collections;
  } catch (error) {
    console.error("Failed to fetch GEE collections:", error);
    throw error;
  }
};

// Get collection metadata
export const getCollectionMetadata = async (
  collectionId: string
): Promise<GEEMetadata> => {
  try {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/collections/${collectionId}`
    );
    return response.metadata;
  } catch (error) {
    console.error("Failed to fetch collection metadata:", error);
    throw error;
  }
};

// Generate satellite imagery
export const generateSatelliteImagery = async (
  collection: string,
  startDate: string,
  endDate: string,
  bounds?: Bounds,
  zoom?: number,
  x?: number,
  y?: number
): Promise<{ tileUrl: string; collection: string; dateRange: DateRange }> => {
  try {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/imagery`, {
      method: "POST",
      body: JSON.stringify({
        collection,
        startDate,
        endDate,
        bounds,
        zoom,
        x,
        y,
      }),
    });
    return response;
  } catch (error) {
    console.error("Failed to generate satellite imagery:", error);
    throw error;
  }
};

// Get time series data
export const getTimeSeriesData = async (
  collection: string,
  startDate: string,
  endDate: string,
  bounds?: Bounds,
  interval: string = "month"
): Promise<{
  timeSeries: TimeSeriesData[];
  collection: string;
  dateRange: DateRange;
}> => {
  try {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/timeseries`,
      {
        method: "POST",
        body: JSON.stringify({
          collection,
          startDate,
          endDate,
          bounds,
          interval,
        }),
      }
    );
    return response;
  } catch (error) {
    console.error("Failed to get time series data:", error);
    throw error;
  }
};

// Create animation frames
export const createAnimationFrames = async (
  collection: string,
  startDate: string,
  endDate: string,
  bounds?: Bounds,
  frameCount: number = 10
): Promise<{
  frames: AnimationFrame[];
  collection: string;
  dateRange: DateRange;
  frameCount: number;
}> => {
  try {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/animation`,
      {
        method: "POST",
        body: JSON.stringify({
          collection,
          startDate,
          endDate,
          bounds,
          frameCount,
        }),
      }
    );
    return response;
  } catch (error) {
    console.error("Failed to create animation frames:", error);
    throw error;
  }
};

// Helper function to convert GEE collection ID to basemap type
export const geeCollectionToBasemapType = (collectionId: string): string => {
  return `gee-${collectionId}`;
};

// Helper function to convert basemap type to GEE collection ID
export const basemapTypeToGeeCollection = (
  basemapType: string
): string | null => {
  if (basemapType.startsWith("gee-")) {
    return basemapType.replace("gee-", "");
  }
  return null;
};

// Helper function to get default date range for a collection
export const getDefaultDateRange = (collectionId: string): DateRange => {
  const now = new Date();
  const endDate = now.toISOString().split("T")[0];

  // Default to 3 months ago
  const startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate,
  };
};

// Helper function to validate date range
export const validateDateRange = (
  startDate: string,
  endDate: string
): boolean => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();

  if (start >= end) {
    throw new Error("Start date must be before end date");
  }

  if (end > now) {
    throw new Error("End date cannot be in the future");
  }

  const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays > 365) {
    throw new Error("Date range cannot exceed 1 year");
  }

  return true;
};
